import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  CbButtonModule,
  CbDropdownMenuModule,
  CbIconModule,
  CbModalModule,
  CbTextAreaModule,
} from '@chubb/ui-components';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { Subscription } from 'rxjs';
import { PolicyData } from '../../../interfaces/policy-data.model';
import { BaseWidgetComponent } from '../../../shared/components/base.widget.component';
import { ReferenceDataSet } from '../../../shared/services/reference-data.types';
import { PremiumBreakdownComponent } from '../premium-breakdown/premium-breakdown.component';

@Component({
  selector: 'app-total-premium',
  imports: [
    CommonModule,
    CbModalModule,
    CbButtonModule,
    PremiumBreakdownComponent,
    CbTextAreaModule,
    TieredMenuModule,
    CbDropdownMenuModule,
    MenuModule,
    ReactiveFormsModule,
    ProgressSpinnerModule,
    CbIconModule,
  ],
  templateUrl: './total-premium.component.html',
  styleUrl: './total-premium.component.scss',
})
export class TotalPremiumComponent extends BaseWidgetComponent {
  override _dataSubscription: Subscription = new Subscription();

  open: boolean = false;
  openSaveModal: boolean = false;
  footerActionList: MenuItem[] = [];
  saveInProgress: boolean = false;
  saveComment: FormControl = new FormControl('', [Validators.required]);
  chosenFrequency = '';

  override ngOnInit() {
    super.ngOnInit(); // call parent init if necessary
    this.chosenFrequency = this.sharedPolicyData?.policy?.paymentFrequency ?? '';

    this._dataSubscription = this._baseDataService.data$.subscribe(data => {
      if (data) {
        this.sharedPolicyData = structuredClone(data);
        this._populateFooterActionList();
      }
    });
    if (this._dataSubscription) {
      this.addSubscription(this._dataSubscription);
    }
  }

  get quoteHasErrors(): boolean {
    return (
      this.sharedPolicyData?.evaluationResponse != null &&
      this.sharedPolicyData?.evaluationResponse?.errors != null &&
      this.sharedPolicyData?.evaluationResponse?.errors?.length > 0 &&
      this.sharedPolicyData?.evaluationResponse?.errors.some(error => !error.message.startsWith('Remove Cover - '))
    );
  }

  get paymentFrequency(): ReferenceDataSet {
    return this.getRefDS('paymentFrequencyDesc') ?? [];
  }

  setChosenPaymentFrequency(frequency: string) {
    this.chosenFrequency = frequency;
  }

  get paymentFrequencyLabel(): string {
    return this._baseGetValByCode('paymentFrequency', this.chosenFrequency) ?? '';
  }

  get coverageCodeVals(): { code: string; val: string }[] {
    const vals: { code: string; val: string }[] = [];
    for (const coverage of this.sharedPolicyData?.coverages!) {
      const val = { code: coverage?.code!, val: this._baseGetValByCode('coverageCode', coverage?.code ?? '')! };
      vals.push(val);
    }
    return vals;
  }

  handleViewBreakdownModal() {
    this.open = !this.open;
  }

  backToDashboard() {
    let policyNumber: string | null = this._route.snapshot.queryParamMap.get('policyNumber');

    if (policyNumber) {
      if (policyNumber?.split('.')?.length > 1) {
        policyNumber = policyNumber?.split('.')[0];
      }
    } else {
      return;
    }

    const urlTree = this._router.createUrlTree(['/dashboard'], {
      queryParams: { policyNumber },
    });
    const url = this._router.serializeUrl(urlTree);

    window.open(url, '_self');
  }

  handleSaveProgressModal() {
    this.openSaveModal = !this.openSaveModal;
  }

  /**
   * Saves the user's progress for the current quote variant.
   * If there are any quote errors, the save operation will not proceed.
   * Upon successful save, redirects the user to the Coverage screen for the saved policy.
   * Displays an error message on failure.
   *
   * @returns {void}
   */
  saveProgress() {
    if (!this.saveComment.valid || this.saveComment.value === '') {
      return;
    }
    if (Object.keys(this._baseDataService.get('updatedQuote')).length > 0) {
      const data = structuredClone(this._baseDataService.get('updatedQuote'));
      if (Object.hasOwn(data, 'evaluationResponse')) {
        if (data.evaluationResponse.errors.length > 0) {
          return;
        }
        delete data.evaluationResponse;
      }
      data.policy.comment = this.saveComment.value ?? '';

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      this._saveQuote(data).catch(error => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this._utilsService.reAttemptIfTokenExpiry(() => this._saveQuote(data)).catch((reAttemptError: string) => {
          this.showError(
            `Save Progress Failure: An error has occurred, please call the Chubb Life team on 0508 464 999 for assistance.`,
          );
        });
      });
    }
  }

  protected async _saveQuote(data: PolicyData): Promise<void> {
    this.saveInProgress = true;
    return (
      this._httpPolicyDataService
        .saveQuoteVariant(data)
        .then((response: { quoteNumber: string }) => {
          this.saveComment.reset();

          const policyNumber = response.quoteNumber;
          const urlTree = this._router.createUrlTree(['/coverage'], {
            queryParams: { policyNumber },
          });
          const url = this._router.serializeUrl(urlTree);

          window.open(url, '_self');
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch(err => {
          throw err;
        })
        .finally(() => {
          this.handleSaveProgressModal();
          this.saveInProgress = false;
        })
    );
  }

  private _populateFooterActionList() {
    this.footerActionList = [
      {
        label: 'View breakdown & Payment Frequency',
        disabled: false,
        command: () => {
          this.handleViewBreakdownModal();
        },
      },
      {
        label: 'Save Progress',
        disabled: !this._baseQuoteHasChanged || this.sharedPolicyData?.evaluationResponse?.errors?.length! > 0,
        command: () => {
          this.handleSaveProgressModal();
        },
      },
      {
        label: 'Return to Dashboard',
        disabled: false,
        command: () => {
          this.backToDashboard();
        },
      },
      {
        separator: true,
      },
    ];
  }
}
